import { gidToId } from '@shared/shop-api/core/helper'
import {
  getMetafieldBoolean,
  getMetafieldDateTime,
  getMetafieldDimension,
  getMetafieldJson,
  getMetafieldList,
  getMetafieldString,
  getMetafieldVolume,
  getMetafieldWeight,
} from '@shared/shop-api/metafield/mapper'
import {
  isPromotedProductDiscountRuleAmountFragment,
  isPromotedProductDiscountRulePercentageFragment,
  isPromotedProductTriggerRuleAmountFragment,
  isPromotedProductTriggerRuleQuantityFragment,
} from '@shared/shop-api/product/attributes/check'
import type {
  BatteryUnNumber,
  PromotedProductDiscountRuleAmountFragment,
  PromotedProductDiscountRulePercentageFragment,
  PromotedProductFragment,
  PromotedProductTriggerRuleAmountFragment,
  PromotedProductTriggerRuleQuantityFragment,
  PromotedProductsFragment,
} from '@shared/shop-api/product/attributes/types'
import type { MetafieldFragment } from '@shared/types/storefront/graphql'

const getStyleDisplay = (metafields: (MetafieldFragment | null)[]) => {
  const style = getMetafieldString(metafields, 'style')
  const styleSuffix = getMetafieldString(metafields, 'shopify_style_suffix')
  return styleSuffix ? `${style} ${styleSuffix}` : style
}

const getStyleUntranslated = (metafields: (MetafieldFragment | null)[]) => {
  const styleUntranslated = getMetafieldString(metafields, 'style_untranslated')
  const styleSuffix = getMetafieldString(metafields, 'shopify_style_suffix')
  return styleSuffix ? `${styleUntranslated} ${styleSuffix}` : styleUntranslated
}

export const toProductAttributes = (
  metafields: (MetafieldFragment | null)[]
) => {
  const edition = getMetafieldList(metafields, 'edition')

  return {
    brand: import.meta.env.PUBLIC_BRAND,
    batteryUnNumber: getMetafieldString<BatteryUnNumber>(
      metafields,
      'battery_un_number'
    ),
    edition,
    collection: getMetafieldList(metafields, 'collection'),
    colors: getMetafieldList(metafields, 'colors'),
    depth: getMetafieldDimension(metafields, 'depth'),
    featureDescription: getMetafieldString(metafields, 'feature_description'),
    shortDescription: getMetafieldString(metafields, 'description_short'),
    longDescription: getMetafieldString(metafields, 'description_long'),
    materialDescription: getMetafieldString(metafields, 'material_description'),
    instructionsForUse: getMetafieldString(metafields, 'instructions_for_use'),
    height: getMetafieldDimension(metafields, 'height'),
    width: getMetafieldDimension(metafields, 'width'),
    launchDate: getMetafieldDateTime(metafields, 'launch_date'),
    mainCategory: getMetafieldString(metafields, 'main_category'),
    model: getMetafieldString(metafields, 'model'),
    name: getMetafieldString(metafields, 'name'),
    restockDate: getMetafieldDateTime(metafields, 'restock_date'),
    volume: getMetafieldVolume(metafields, 'volume'),
    responsibility: getMetafieldString(metafields, 'responsibility'),
    season: getMetafieldString(metafields, 'season'),
    style: getMetafieldString(metafields, 'style'),
    styleDisplay: getStyleDisplay(metafields),
    styleSwitcherColorCode: getMetafieldString(
      metafields,
      'style_switcher_color_code'
    ),
    subCategory: getMetafieldString(metafields, 'sub_category'),
    type: getMetafieldString(metafields, 'type'),
    weight: getMetafieldWeight(metafields, 'weight'),
    caseableSku: getMetafieldString(metafields, 'caseable_sku'),
    features: getMetafieldList(metafields, 'features'),
    productContent: getMetafieldString(
      metafields,
      'product_content_collection'
    ),
    materialType: getMetafieldString(metafields, 'material_type'),
    isNew: getMetafieldBoolean(metafields, 'new'),
    careInstructions: getMetafieldString(metafields, 'care_instructions'),
    deliveryTime: getMetafieldString(metafields, 'delivery_time'),
    themeWorld: getMetafieldString(metafields, 'theme_world'),
    promotedProducts: getMetafieldJson<PromotedProductsFragment>(
      metafields,
      'promoted_products',
      []
    ).map(toPromotedProduct),
    modelUntranslated: getMetafieldString(metafields, 'model_untranslated'),
    styleUntranslated: getStyleUntranslated(metafields),
    nameUntranslated: getMetafieldString(metafields, 'name_untranslated'),
    themeWorldContentCollection: getMetafieldString(
      metafields,
      'theme_world_content_collection'
    ),

    /**
     * @deprecated use `materialType` instead
     */
    outerMaterialType: getMetafieldString(metafields, 'material_type'),
    /**
     * @deprecated use `styleSwitcherColorCode` instead
     */
    colorCode: getMetafieldString(metafields, 'style_switcher_color_code'),
    /**
     * @deprecated use `edition` instead
     */
    editions: edition ? [edition] : [],
    /**
     * @deprecated use `productContent` instead
     */
    additionalContent: getMetafieldString(
      metafields,
      'product_content_collection'
    ),
  }
}

export const toPromotedProduct = (
  promotedProducts: PromotedProductFragment
) => {
  const triggerRule = toPromotedProductTriggerRule(promotedProducts.triggerRule)

  const items = promotedProducts.items
    .map((gid) => {
      if (!gid) {
        return undefined
      }

      try {
        return gidToId(gid)
      } catch {
        return undefined
      }
    })
    .filter((id): id is number => id !== undefined)

  return {
    promotionTitle: promotedProducts.promotionTitle,
    discountId: promotedProducts.discountId,
    startsAt: promotedProducts.startsAt
      ? new Date(promotedProducts.startsAt)
      : undefined,
    endsAt: promotedProducts.endsAt
      ? new Date(promotedProducts.endsAt)
      : undefined,
    triggerRule,
    discountRule: {
      quantity: parseInt(promotedProducts.discountRule.quantity),
      type: toPromotedProductDiscountRuleType(
        promotedProducts.discountRule.type
      ),
    },
    items,
  }
}

export const toPromotedProductTriggerRule = (
  triggerRule: PromotedProductFragment['triggerRule']
) => {
  if (isPromotedProductTriggerRuleAmountFragment(triggerRule)) {
    return toPromotedProductTriggerRuleAmount(triggerRule)
  }

  if (isPromotedProductTriggerRuleQuantityFragment(triggerRule)) {
    return toPromotedProductTriggerRuleQuantity(triggerRule)
  }

  return undefined
}

export const toPromotedProductDiscountRuleType = (
  type: PromotedProductFragment['discountRule']['type']
) => {
  if (isPromotedProductDiscountRulePercentageFragment(type)) {
    return toPromotedProductDiscountRulePercentage(type)
  }
  if (isPromotedProductDiscountRuleAmountFragment(type)) {
    return toPromotedProductDiscountRuleAmount(type)
  }

  return undefined
}

export const toPromotedProductDiscountRulePercentage = (
  percentageRule: PromotedProductDiscountRulePercentageFragment
) => {
  return {
    percentage: parseFloat(percentageRule.percentage),
  }
}

export const toPromotedProductDiscountRuleAmount = (
  amountRule: PromotedProductDiscountRuleAmountFragment
) => {
  return {
    amount: parseFloat(amountRule.amount),
    currencyCode: amountRule.currencyCode,
  }
}

export const toPromotedProductTriggerRuleQuantity = (
  quantityRule: PromotedProductTriggerRuleQuantityFragment
) => {
  return {
    quantity: parseInt(quantityRule.quantity),
  }
}

export const toPromotedProductTriggerRuleAmount = (
  amountRule: PromotedProductTriggerRuleAmountFragment
) => {
  return {
    amount: parseFloat(amountRule.amount),
  }
}
